import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import {
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@material-ui/icons'
import { toast } from 'react-toastify'
import * as NinetyNineFoodService from '../../../services/99food'
import { useMerchant } from '../../../store/global/useMerchant'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  button: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  stepperRoot: {
    backgroundColor: 'transparent',
  },
  infoBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  logo99: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#f97316',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
    flexShrink: 0,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  appShopIdBox: {
    backgroundColor: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontFamily: 'monospace',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    textAlign: 'center',
  },
}))

function Integracao99Food() {
  const merchant = useMerchant(st => st.data.merchant)
  const classes = useStyles()

  const storeId = merchant?.id || null

  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(true)

  // Step 1
  const [appShopId, setAppShopId] = useState('')

  // Step 2
  const [authUrl, setAuthUrl] = useState('')
  const [urlLoading, setUrlLoading] = useState(false)

  // Step 3
  const [confirmLoading, setConfirmLoading] = useState(false)

  // Status da conexão
  const [tokenStatus, setTokenStatus] = useState(null)

  const checkStatus = useCallback(async () => {
    if (!storeId) {
      setCheckingStatus(false)
      return
    }
    setCheckingStatus(true)
    try {
      const data = await NinetyNineFoodService.getTokenStatus(storeId)
      setTokenStatus(data)
    } catch (error) {
      setTokenStatus({ connected: false })
    } finally {
      setCheckingStatus(false)
    }
  }, [storeId])

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  // Step 1 → Step 2: busca URL de autorização
  const handleGetAuthUrl = async () => {
    if (!appShopId.trim()) {
      toast.error('Informe o App Shop ID da sua loja', { theme: 'colored' })
      return
    }
    setUrlLoading(true)
    try {
      const data = await NinetyNineFoodService.getAuthorizationUrl(appShopId.trim())
      setAuthUrl(data.url)
      setActiveStep(1)
      toast.success('URL de autorização gerada!', { theme: 'colored' })
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao gerar URL. Verifique o App Shop ID.'
      toast.error(msg, { theme: 'colored' })
    } finally {
      setUrlLoading(false)
    }
  }

  // Step 2 → Step 3: abre o portal 99Food
  const handleOpenPortal = () => {
    window.open(authUrl, '_blank')
    toast.info('Autorize sua loja no portal da 99Food e volte aqui', { theme: 'colored' })
  }

  // Step 3: busca e salva o token
  const handleConfirmToken = async () => {
    setConfirmLoading(true)
    try {
      await NinetyNineFoodService.initToken(appShopId.trim(), storeId)
      toast.success('Loja conectada à 99Food com sucesso!', { theme: 'colored' })
      setActiveStep(2)
      checkStatus()
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao confirmar token. Tente novamente após autorizar no portal.'
      toast.error(msg, { theme: 'colored' })
    } finally {
      setConfirmLoading(false)
    }
  }

  const handleReset = () => {
    setActiveStep(0)
    setAppShopId('')
    setAuthUrl('')
    checkStatus()
  }

  const steps = [
    {
      label: 'Informe o App Shop ID',
      content: (
        <Box>
          <Typography variant="body2" gutterBottom>
            O <strong>App Shop ID</strong> é o identificador da sua loja no sistema da 99Food.
            Você recebe esse código ao se cadastrar como parceiro na plataforma.
          </Typography>
          <Alert severity="info" className={classes.infoBox}>
            Não sabe seu App Shop ID? Entre em contato com o suporte da 99Food ou acesse o portal do parceiro.
          </Alert>
          <TextField
            fullWidth
            label="App Shop ID"
            variant="outlined"
            value={appShopId}
            onChange={(e) => setAppShopId(e.target.value.trim())}
            placeholder="Ex: petland-sorocaba-01"
            margin="normal"
            helperText="ID da sua loja no sistema da 99Food"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleGetAuthUrl}
            disabled={urlLoading || !appShopId.trim()}
            className={classes.button}
          >
            {urlLoading ? <CircularProgress size={24} /> : 'Próximo'}
          </Button>
        </Box>
      ),
    },
    {
      label: 'Autorizar no portal 99Food',
      content: (
        <Box>
          <Typography variant="body2" gutterBottom>
            Clique no botão abaixo para abrir o portal da 99Food e autorizar a conexão da sua loja.
          </Typography>
          <div className={classes.appShopIdBox}>
            {appShopId}
          </div>
          <Alert severity="warning" className={classes.infoBox}>
            Após autorizar no portal, volte aqui e clique em <strong>"Já autorizei"</strong>.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenPortal}
            className={classes.button}
            disabled={!authUrl}
          >
            Abrir Portal 99Food
          </Button>
          <Box mt={3}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleConfirmToken}
              disabled={confirmLoading}
              className={classes.button}
            >
              {confirmLoading ? <CircularProgress size={24} /> : 'Já autorizei ✓'}
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Conexão concluída',
      content: (
        <Box>
          <Alert severity="success" className={classes.infoBox}>
            <strong>Sua loja está conectada à 99Food!</strong><br />
            Os pedidos serão recebidos automaticamente e o cardápio pode ser sincronizado pelo painel.
          </Alert>
          <Typography variant="body2" color="textSecondary" paragraph>
            O token de autenticação será renovado automaticamente pelo sistema.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReset}
            className={classes.button}
          >
            Reconfigurar
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <div className={classes.root}>

      {/* Status da conexão */}
      {checkingStatus && (
        <Card className={classes.card}>
          <CardContent>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <CircularProgress size={22} />
              <Typography variant="body2">Verificando status da integração...</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {!checkingStatus && tokenStatus?.connected && (
        <Card className={classes.card}>
          <CardContent>
            <Alert
              severity={tokenStatus.isExpired ? 'error' : tokenStatus.isAboutToExpire ? 'warning' : 'success'}
              icon={tokenStatus.isExpired ? <ErrorIcon /> : <CheckCircleIcon />}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  {tokenStatus.isExpired
                    ? 'Token expirado – reconecte sua loja'
                    : tokenStatus.isAboutToExpire
                    ? 'Token próximo do vencimento'
                    : 'Loja conectada à 99Food'}
                  <Chip
                    size="small"
                    label={tokenStatus.appShopId}
                    className={classes.statusChip}
                    variant="outlined"
                  />
                </Typography>
                {tokenStatus.expiresAt && (
                  <Typography variant="body2">
                    <strong>{tokenStatus.isExpired ? 'Expirou em:' : 'Expira em:'}</strong>{' '}
                    {new Date(tokenStatus.expiresAt).toLocaleString('pt-BR')}
                  </Typography>
                )}
                {tokenStatus.updatedAt && (
                  <Typography variant="body2">
                    <strong>Última atualização:</strong>{' '}
                    {new Date(tokenStatus.updatedAt).toLocaleString('pt-BR')}
                  </Typography>
                )}
              </Box>
              <Box mt={2} display="flex" style={{ gap: 8 }}>
                <Tooltip title="Atualizar status">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={checkStatus}
                    disabled={checkingStatus}
                  >
                    Atualizar
                  </Button>
                </Tooltip>
                {(tokenStatus.isExpired || tokenStatus.isAboutToExpire) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleReset}
                  >
                    Reconectar
                  </Button>
                )}
              </Box>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Wizard de conexão – exibe apenas se não conectado ou token expirado */}
      {!checkingStatus && (!tokenStatus?.connected || tokenStatus?.isExpired) && (
        <>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.logoArea}>
                <div className={classes.logo99}>99</div>
                <div>
                  <Typography variant="h6">Como conectar sua loja à 99Food</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Siga os passos abaixo para integrar sem precisar falar com o suporte.
                  </Typography>
                </div>
              </div>
              <Typography variant="body2" paragraph>
                <strong>Passo 1:</strong> Informe o <em>App Shop ID</em> da sua loja (fornecido pela 99Food ao seu cadastro).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Passo 2:</strong> Acesse o portal da 99Food pelo link gerado e autorize a integração.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Passo 3:</strong> Clique em <em>"Já autorizei"</em> — a loja estará conectada e pronta para receber pedidos.
              </Typography>
            </CardContent>
          </Card>

          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Conectar loja à 99Food
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure a integração de forma autônoma, sem precisar entrar em contato com a equipe.
              </Typography>

              <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepperRoot}>
                {steps.map((step) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                    <StepContent>{step.content}</StepContent>
                  </Step>
                ))}
              </Stepper>

              {activeStep < steps.length - 1 && activeStep > 0 && (
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

    </div>
  )
}

export default Integracao99Food
